import { useEffect, useRef } from 'react';
import * as d3 from 'd3';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Tooltip from '@mui/material/Tooltip';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';

export default function PCAMatrixPanel({ pcaResult, cellHeight = 8 }) {
  const ref = useRef<HTMLDivElement | null>(null);
  const legendRef = useRef<HTMLDivElement | null>(null);

  // Use pcaResult.pcaMatrix everywhere instead of pcaMatrix
  useEffect(() => {
    const pcaMatrix = pcaResult?.pcaMatrix;
    console.log('[PCAMatrixPanel][useEffect] pcaMatrix changed:', pcaMatrix);
    if (!pcaMatrix || !pcaMatrix.length) return;
    const nRows = pcaMatrix.length;
    const nCols = pcaMatrix[0].length;

    // Remove previous svg
    d3.select(ref.current).selectAll('svg').remove();

    // Responsive width: fit parent or max 100%
    const width = Math.max(nCols, 320); // minimum width for visibility
    const height = nRows * cellHeight;

    // Flatten for color scale
    const allValues = pcaMatrix.flat();
    const min = d3.min(allValues);
    const max = d3.max(allValues);

    const colorScale = d3.scaleLinear()
      .domain([min, 0, max])
      .range(['#2166ac', '#f7f7f7', '#b2182b']);

    const svg = d3.select(ref.current)
      .append('svg')
      .attr('width', width)
      .attr('height', height)
      .style('display', 'block')
      .style('width', '100%')
      .style('background', '#f8fafc');

    svg.selectAll('g')
      .data(pcaMatrix)
      .enter()
      .append('g')
      .attr('transform', (d, i) => `translate(0,${i * cellHeight})`)
      .selectAll('rect')
      .data(d => d)
      .enter()
      .append('rect')
      .attr('x', (d, j) => j * (width / nCols))
      .attr('y', 0)
      .attr('width', width / nCols)
      .attr('height', cellHeight)
      .attr('fill', d => colorScale(d));
  }, [pcaResult, cellHeight]);

  // --- Separate effect for legend ---
  useEffect(() => {
    console.log('[PCAMatrixPanel][useEffect] legend changed');
    const pcaMatrix = pcaResult?.pcaMatrix;
    if (!legendRef.current || !pcaMatrix || !pcaMatrix.length) return;
    // Remove previous legend svg
    d3.select(legendRef.current).selectAll('svg').remove();

    const allValues = pcaMatrix.flat();
    const min = d3.min(allValues);
    const max = d3.max(allValues);

    const colorScale = d3.scaleLinear()
      .domain([min, 0, max])
      .range(['#2166ac', '#f7f7f7', '#b2182b']);

    const legendWidth = 120;
    const legendHeight = 12;
    const legendSvg = d3.select(legendRef.current)
      .append('svg')
      .attr('width', legendWidth)
      .attr('height', legendHeight + 16);

    // Gradient
    const defs = legendSvg.append('defs');
    const gradientId = 'pca-matrix-legend-gradient';
    const gradient = defs.append('linearGradient')
      .attr('id', gradientId)
      .attr('x1', '0%').attr('y1', '0%')
      .attr('x2', '100%').attr('y2', '0%');

    gradient.append('stop').attr('offset', '0%').attr('stop-color', colorScale(min));
    gradient.append('stop').attr('offset', '50%').attr('stop-color', colorScale(0));
    gradient.append('stop').attr('offset', '100%').attr('stop-color', colorScale(max));

    legendSvg.append('rect')
      .attr('x', 0)
      .attr('y', 0)
      .attr('width', legendWidth)
      .attr('height', legendHeight)
      .style('fill', `url(#${gradientId})`)
      .style('stroke', '#ccc')
      .style('stroke-width', 1);

    // Min, 0, Max labels
    legendSvg.append('text')
      .attr('x', 0)
      .attr('y', legendHeight + 12)
      .attr('text-anchor', 'start')
      .attr('font-size', 10)
      .attr('fill', '#555')
      .text(min.toFixed(2));
    legendSvg.append('text')
      .attr('x', legendWidth / 2)
      .attr('y', legendHeight + 12)
      .attr('text-anchor', 'middle')
      .attr('font-size', 10)
      .attr('fill', '#555')
      .text('0');
    legendSvg.append('text')
      .attr('x', legendWidth)
      .attr('y', legendHeight + 12)
      .attr('text-anchor', 'end')
      .attr('font-size', 10)
      .attr('fill', '#555')
      .text(max.toFixed(2));
  }, [pcaResult, legendRef]);

  const pcaMatrix = pcaResult?.pcaMatrix;
  if (!pcaMatrix || !pcaMatrix.length) return null;

  return (
    <Box
      sx={{
        width: '100%',
        background: '#fff',
        border: '1px solid #e0e0e0',
        display: 'flex',
        flexDirection: 'row',
        alignItems: 'center',
        borderRadius: 2,
        px: 1,
        py: 1,
        minHeight: 24,
        maxHeight: 24,
        overflow: 'hidden',
        m: 0,
      }}
    >
      {/* Title and info */}
      <Box sx={{ display: 'flex', alignItems: 'center', minWidth: 180, mr: 1 }}>
        <Typography variant="caption" sx={{ color: 'text.secondary', mr: 0.5 }}>
          PCA Matrix (basis vectors)
        </Typography>
        <Tooltip
          title={
            <>
              This grid shows the principal component basis vectors (rows) for the embedding space after PCA.<br />
              Each cell&apos;s color represents the value of a component in the original embedding dimension.<br />
              <b>Legend:</b> The color bar shows the mapping from value to color.
            </>
          }
          placement="top"
          arrow
        >
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <HelpOutlineIcon fontSize="small" sx={{ color: 'text.secondary', cursor: 'pointer', mr: 0 }} />
          </Box>
        </Tooltip>
      </Box>
      {/* Legend */}
      <Box
        ref={legendRef}
        sx={{
          width: 130,
          height: 28,
          userSelect: 'none',
          display: 'inline-block',
          mr: 0,
        }}
      />
      {/* Matrix visualization */}
      <Box
        ref={ref}
        sx={{
          display: 'inline-block',
          background: '#f8fafc',
          borderRadius: 1,
          height: cellHeight * (pcaMatrix.length || 1),
          border: '1px solid rgb(174, 174, 174)',
          alignItems: 'center',
          overflowX: 'auto',
          verticalAlign: 'middle',
        }}
      />
    </Box>
  );
}